import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Modal, RadioGroup } from '@rabbit/elements/shared-components';
import { toast } from 'react-toastify';
import { nestApiUpdateHoldingConsumer } from '@rabbit/bizproc/core';
import CreateNewSelectUser from '../forms/CreateNewSelectUser/CreateNewSelectUser';
import ModalCreateNewConsumerForm from '../ModalCreateNewConsumerForm/ModalCreateNewConsumerForm';

export interface ModalCreateNewSelectUserProps {
  handleClose: () => void;
  onSubmit?: (data: any) => void;
  holdingId: string;
  holdingLink: string;
  data?: any;
  onCustomerChanged?: () => void;
  oldEmail?: string;
}

export function ModalCreateNewSelectUser({
  handleClose,
  onSubmit,
  holdingId,
  holdingLink,
  data,
  onCustomerChanged,
  oldEmail,
}: ModalCreateNewSelectUserProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showNewConsumerModal, setShowNewConsumerModal] = useState(false);
  const [changeType, setChangeType] = useState('customer'); // 'customer' or 'ownership'
  const [showContinue, setShowContinue] = useState(false); // Track if continue button has been clicked
  const [transferReason, setTransferReason] = useState('');

  const handleSelectUserData = async (data: any) => {
    if (!data) return; // User cancelled

    if (
      oldEmail &&
      data.consumer?.value &&
      data.consumer.value.toLowerCase() === oldEmail.toLowerCase()
    ) {
      setIsLoading(false);
      toast.error(t('message.oldAndNewEmailSame'));
      return;
    }

    setIsLoading(true);
    try {
      // For existing customer
      if (data.consumer?.id) {
        await nestApiUpdateHoldingConsumer({
          holdingLink: holdingId,
          consumerLink: data.consumer.id,
          tenantLink: t('tenantLink'),
          transferReason: transferReason,
        });
        toast.success(t('message.customerChangedSuccessfully'));
        if (onCustomerChanged) {
          onCustomerChanged();
        }
        handleClose();
      }
    } catch (error) {
      console.error('Error changing customer:', error);
      toast.error(t('message.failedToChangeCustomer'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewConsumerData = async (data: any) => {
    if (!data) {
      setShowNewConsumerModal(false);
      return; // User cancelled
    }

    // Check if the new consumer email is the same as the old email
    if (
      oldEmail &&
      data.consumer_email &&
      data.consumer_email.toLowerCase() === oldEmail.toLowerCase()
    ) {
      toast.error(t('message.oldAndNewEmailSame'));
      setShowNewConsumerModal(false);
      return;
    }

    setIsLoading(true);
    try {
      // Create new consumer and update holding
      await nestApiUpdateHoldingConsumer({
        holdingLink: holdingId,
        newConsumer: {
          firstName: data.consumer_first_name,
          lastName: data.consumer_last_name,
          email: data.consumer_email,
          telephone: data.consumer_telephone,
          address: data.consumer_address,
          isCommercial: data.consumer_is_commercial,
        },
        tenantLink: t('tenantLink'),
        transferReason: transferReason,
      });
      toast.success(t('message.newCustomerCreatedAndAssignedSuccessfully'));
      if (onCustomerChanged) {
        onCustomerChanged();
      }
      handleClose();
    } catch (error) {
      console.error('Error changing customer:', error);
      toast.error(t('message.failedToChangeCustomer'));
      setShowNewConsumerModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (nextStep: string, data: any) => {
    if (nextStep === 'add-product' || nextStep === 'choose-product') {
      if (onSubmit) {
        onSubmit(data);
      } else {
        handleSelectUserData(data);
      }
    } else if (nextStep === 'new-consumer') {
      setShowNewConsumerModal(true);
    }
  };

  return (
    <>
      <Modal
        kind="generic"
        settings={{
          handleClose,
          title: showContinue
            ? t('general.selectCustomer')
            : t('general.selectActionType'),
          childrenClassName: 'overflow-visible',
        }}
        className="lg:w-[45.25rem]"
      >
        <div className="">
          {!showContinue && (
            <div className="mb-6 p-4 pb-0">
              <RadioGroup
                value={changeType}
                onChange={setChangeType}
                className="mb-4"
              >
                <div className="space-y-2">
                  <RadioGroup.Option
                    value="customer"
                    className={({ checked }) =>
                      `${
                        checked ? 'border-green-900 bg-gray-50' : 'bg-white'
                      } relative flex cursor-pointer rounded-lg border p-3 focus:outline-none`
                    }
                  >
                    {({ checked }) => (
                      <div className="flex w-full items-center justify-between">
                        <div className="flex items-center">
                          <div className="text-sm">
                            <RadioGroup.Label
                              as="p"
                              className={`font-medium ${
                                checked ? 'text-green-900' : 'text-gray-900'
                              }`}
                            >
                              {t('general.changeCustomer')}
                            </RadioGroup.Label>
                            <RadioGroup.Description
                              as="span"
                              className={`inline ${
                                checked ? 'text-green-900' : 'text-gray-500'
                              }`}
                            >
                              {t('general.assignToDifferentCustomer')}
                            </RadioGroup.Description>
                          </div>
                        </div>
                        <div
                          className={`shrink-0 rounded-full border ${
                            checked
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-300'
                          } h-5 w-5`}
                        >
                          {checked && (
                            <div className="flex h-full items-center justify-center">
                              <div className="h-2 w-2 rounded-full bg-white"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option
                    value="ownership"
                    disabled={true}
                    className={({ checked }) =>
                      `${
                        checked ? 'border-green-500 bg-blue-50' : 'bg-white'
                      } relative flex cursor-not-allowed rounded-lg border p-3 opacity-50 focus:outline-none`
                    }
                  >
                    {({ checked }) => (
                      <div className="flex w-full items-center justify-between">
                        <div className="flex items-center">
                          <div className="text-sm">
                            <RadioGroup.Label
                              as="p"
                              className={`font-medium ${
                                checked ? 'text-green-900' : 'text-gray-900'
                              }`}
                            >
                              {t('general.transferOwnership')}
                            </RadioGroup.Label>
                            <RadioGroup.Description
                              as="span"
                              className={`inline ${
                                checked ? 'text-green-700' : 'text-gray-500'
                              }`}
                            >
                              {t('general.transferOwnershipDisabled')}
                            </RadioGroup.Description>
                          </div>
                        </div>
                        <div
                          className={`shrink-0 rounded-full border ${
                            checked
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-300'
                          } h-5 w-5`}
                        >
                          {checked && (
                            <div className="flex h-full items-center justify-center">
                              <div className="h-2 w-2 rounded-full bg-white"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </RadioGroup.Option>
                </div>
              </RadioGroup>

              <div className="mt-4">
                <label
                  htmlFor="transferReason"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('general.transferReason')}
                </label>
                <input
                  type="text"
                  id="transferReason"
                  name="transferReason"
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  placeholder={t('general.enterTransferReason')}
                  className="focus:border-primary-600 mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-base text-gray-900 focus:outline-none"
                />
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  className="rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  onClick={() => {
                    setShowContinue(true);
                  }}
                >
                  {t('general.continue')}
                </button>
              </div>
            </div>
          )}

          {showContinue && (
            <CreateNewSelectUser
              handleClose={handleClose}
              onChange={handleFormChange}
              data={{ type: 'existing', ...data }}
              setLoading={setIsLoading}
              kind="new-registration"
            />
          )}
        </div>
      </Modal>

      {showNewConsumerModal && (
        <ModalCreateNewConsumerForm
          handleClose={() => setShowNewConsumerModal(false)}
          onSubmit={handleNewConsumerData}
          data={{
            customerType: 'new',
          }}
          isLoading={isLoading}
        />
      )}
    </>
  );
}

export default ModalCreateNewSelectUser;
